import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Editor, NodeViewProps } from '@tiptap/core';

/** 1-based position of the blank chip with id `ownId` among every blankChip node currently in
 * the document, in document order. Matched by id (not `getPos()`, which isn't reliably current
 * across renders) since every blank id is already a unique crypto.randomUUID(). */
function computeOrdinal(editor: Editor, ownId: string): number {
  let count = 0;
  let ordinal = 0;
  editor.state.doc.descendants(node => {
    if (node.type.name === 'blankChip') {
      count++;
      if (node.attrs.id === ownId) ordinal = count;
    }
  });
  return ordinal;
}

function BlankChipNodeView({ node, editor }: NodeViewProps) {
  const { t } = useTranslation();
  const id = node.attrs.id as string;
  const [ordinal, setOrdinal] = useState(() => computeOrdinal(editor, id));

  // Recomputed on every doc transaction (not just this node's own attr/position changes via
  // Tiptap's update() prop-diffing) so the number stays live as sibling blanks elsewhere in the
  // document are inserted, removed, or reordered.
  useEffect(() => {
    function recompute() { setOrdinal(computeOrdinal(editor, id)); }
    recompute();
    editor.on('transaction', recompute);
    return () => { editor.off('transaction', recompute); };
  }, [editor, id]);

  const label = t('blocks.fitb.blankChipLabel', { ordinal });

  return (
    <NodeViewWrapper as='span' className='inline-block align-middle' data-drag-handle>
      <span
        contentEditable={false}
        aria-label={label}
        title={label}
        className='inline-flex cursor-grab select-none items-center rounded border border-ws-muted/40 bg-ws-muted/10 px-1.5 py-0.5 font-mono text-xs font-semibold text-ws-muted active:cursor-grabbing'
        style={{ verticalAlign: 'middle', position: 'relative', top: '-2px' }}
      >
        {label}
      </span>
    </NodeViewWrapper>
  );
}

export const BlankChip = Node.create({
  name: 'blankChip',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: el => el.getAttribute('data-blank-id') ?? '',
        renderHTML: attrs => ({ 'data-blank-id': attrs.id }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-blank-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), HTMLAttributes['data-blank-id'] ?? ''];
  },

  addNodeView() {
    return ReactNodeViewRenderer(BlankChipNodeView);
  },
});
